import type { FC, ReactNode } from 'react'

import { motion, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'

/**
 * The hand-drawn marks scattered through the composition.
 *
 * Every one is a handful of stroked path data in a 24x24 box — no illustration
 * library, no image, no icon font — so they inherit `currentColor`, scale
 * without going soft, and cost nothing to load. They are STROKES rather than
 * fills on purpose: a stroke can be drawn on, which is what gives the entrance
 * its marker-pen feel and reads as somebody annotating the board.
 *
 * They are decoration and never carry meaning, so each is hidden from
 * assistive tech and the composition states its own summary instead.
 */

export type DoodleName = 'arrow' | 'loop' | 'hatch' | 'crown' | 'burst' | 'star' | 'zigzag' | 'spark'

export type DoodleTone = 'red' | 'ink'

/** Marker strokes, drawn in the order they appear so the draw-on reads right. */
const DOODLE_PATHS: Record<DoodleName, ReadonlyArray<string>> = {
    arrow: [
        'M19.5 3.2c0.9 9.2-4.6 15.9-14.4 17.8',
        'M5.1 21L11.8 20.4',
        'M5.1 21L8.2 14.6'
    ],
    loop: [
        'M3 20.5C5 12 8.5 5 12 6.5C15 7.8 13 12.5 10.8 10.4C8.6 8.3 12.5 4.2 16 6.6C18.6 8.4 19.8 12 20.2 15.2',
        'M20.2 15.2L17.2 13.2',
        'M20.2 15.2L21.6 12.2'
    ],
    hatch: [
        'M2.5 19.5L7 5.5',
        'M7.2 19.5L11.7 5.5',
        'M11.9 19.5L16.4 5.5',
        'M16.6 19.5L21.1 5.5'
    ],
    crown: ['M3 18.2L4.6 6.4L9.6 11.6L12 4.4L14.4 11.6L19.4 6.4L21 18.2Z'],
    burst: [
        'M12 1.6L14.1 8.6L20.8 5.2L17 11.4L23 13.6L15.9 15.1L18.6 21.8L12.6 17.9L8.2 22.6L9.5 15.6L2.4 16.1L7.4 11.2L1.8 6.6L8.8 8.5Z'
    ],
    star: ['M12 2.4L14.9 9.2L22.2 9.8L16.7 14.6L18.4 21.7L12 17.9L5.6 21.7L7.3 14.6L1.8 9.8L9.1 9.2Z'],
    zigzag: [
        'M6.5 3L10.5 8.6L6.5 12.2L10.5 17.8L6.5 21',
        'M14.5 3L18.5 8.6L14.5 12.2L18.5 17.8L14.5 21'
    ],
    spark: [
        'M12 2.2C12.7 8.6 15.4 11.3 21.8 12C15.4 12.7 12.7 15.4 12 21.8C11.3 15.4 8.6 12.7 2.2 12C8.6 11.3 11.3 8.6 12 2.2Z'
    ]
}

const TONE_CLASS: Record<DoodleTone, string> = {
    red: 'text-brand-red',
    ink: 'text-ink-900'
}

interface HeroDoodleProps {
    name: DoodleName
    tone?: DoodleTone
    /** Milliseconds after mount before the stroke starts drawing. */
    delay?: number
    className?: string
}

export const HeroDoodle: FC<HeroDoodleProps> = ({
    name,
    tone = 'ink',
    delay = 0,
    className
}): ReactNode => {
    const reduced = useReducedMotion()
    const paths = DOODLE_PATHS[name]
    const start = delay / 1000

    return (
        <motion.svg
            viewBox='0 0 24 24'
            fill='none'
            aria-hidden='true'
            initial='hidden'
            animate='shown'
            className={cn('h-auto w-full overflow-visible', TONE_CLASS[tone], className)}
        >
            {paths.map((d, index) => (
                <motion.path
                    key={d}
                    d={d}
                    stroke='currentColor'
                    strokeWidth={2.1}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    variants={{
                        hidden: { pathLength: 0, opacity: 0 },
                        shown: { pathLength: 1, opacity: 1 }
                    }}
                    transition={
                        reduced
                            ? { duration: 0 }
                            : {
                                pathLength: { duration: 0.5, delay: start + index * 0.09, ease: 'easeOut' },
                                opacity: { duration: 0.12, delay: start + index * 0.09 }
                            }
                    }
                />
            ))}
        </motion.svg>
    )
}
